import React, { useState } from 'react';
import { Edit2, Trash2, User, CheckCircle, XCircle } from 'lucide-react';
import { FarmerDto } from '../../../services/api';

interface FarmersListProps {
  farmers: FarmerDto[];
  isLoading: boolean;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export const FarmersList: React.FC<FarmersListProps> = ({
  farmers,
  isLoading,
  onEdit,
  onDelete,
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (farmers.length === 0) {
    return (
      <div className="text-center p-8 bg-slate-50 rounded-lg border border-slate-200 border-dashed">
        <User className="mx-auto mb-3 text-slate-400" size={48} />
        <p className="text-slate-500">No hay agricultores registrados</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Nombre</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">ID Externo</th>
              <th className="text-center px-6 py-4 text-sm font-semibold text-slate-700">Estado</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Creado</th>
              <th className="text-right px-6 py-4 text-sm font-semibold text-slate-700">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {farmers.map((farmer) => (
              <tr key={farmer.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <User size={16} className="text-green-600" />
                    </div>
                    <span className="font-medium text-slate-900">{farmer.name}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <code className="bg-slate-100 px-2 py-1 rounded text-xs font-mono">
                    {farmer.externalId}
                  </code>
                </td>
                <td className="px-6 py-4 text-center">
                  {farmer.isActive ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      <CheckCircle size={14} />
                      Activo
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                      <XCircle size={14} />
                      Inactivo
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-slate-600 text-sm">
                  {new Date(farmer.createdAt).toLocaleDateString('es-ES')}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    {onEdit && (
                      <button
                        onClick={() => onEdit(farmer.id)}
                        className="p-2 hover:bg-blue-50 rounded-lg transition-colors text-blue-600 hover:text-blue-700"
                        title="Editar"
                      >
                        <Edit2 size={18} />
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={() => onDelete(farmer.id)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-600 hover:text-red-700"
                        title="Eliminar"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
